import React from 'react';
import { useTranslation } from 'react-i18next';
import MapIcon from '@mui/icons-material/Map';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { ModernCard, ModernCheckbox, ModernButton } from './ModernComponents';
import MapParamsSimulation from '../Result/Map/MapParamsSimulation';
import './styles/step_parcels.style.scss';

const StepParcels = ({
  searchTerm,
  setSearchTerm,
  viewMapParcels,
  setViewMapParcels,
  mapCoordinates,
  filteredParcels,
  selectedParcels,
  setSelectedParcels,
  exploitationSelected,
  toggleSelection,
}) => {
  const { t } = useTranslation();
  const getTotalSurfaceParcelGroup = (parentSurface, childrenParcels) => {
    const childrenSurface = childrenParcels?.reduce((acc, p) => acc + (p.surface || 0), 0) || 0;
    return (parentSurface + childrenSurface).toFixed(2);
  };

  const getParcelLabel = (parcel) => {
    let suffix = ` (${parcel.surface} ha)`;

    if (parcel.isParent) {
      const childrenParcels = filteredParcels?.filter((p) => p?.parent === parcel?.id);
      const totalSurface = getTotalSurfaceParcelGroup(parcel.surface, childrenParcels);
      suffix += ` - ${t('simulation.step.parcels.grouping')} : ${totalSurface} ha`;
    }

    return (
      <span className="parcelLabel">
        <span className="parcelName">{parcel.name}</span>
        <span className="parcelSuffix">{suffix}</span>
      </span>
    );
  };

  const parentParcels = filteredParcels?.filter((p) => !p.parent) || [];

  const isAllSelected =
    parentParcels.length > 0 && parentParcels.every((p) => selectedParcels.includes(p.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedParcels([]);
    } else {
      setSelectedParcels(filteredParcels?.map((p) => p.id));
    }
  };

  return (
    <div className="stepParcels">
      <ModernCard
        title={t('simulation.common.parcelSelection')}
        icon={MapIcon}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t('common.parcels')}
        headerLeft={
          <ModernButton variant="secondary" onClick={handleSelectAll}>
            {isAllSelected ? t('simulation.common.deselectAll') : t('common.selectAll')}
          </ModernButton>
        }
      >
        {mapCoordinates && (
          <div className="viewModeSelector">
            <button
              className={`viewModeBtn ${!viewMapParcels ? 'active' : ''}`}
              onClick={() => setViewMapParcels(false)}
            >
              <PlaylistAddCheckIcon sx={{ fontSize: 18 }} />
              {t('simulation.step.parcels.list')}
            </button>
            <button
              className={`viewModeBtn ${viewMapParcels ? 'active' : ''}`}
              onClick={() => setViewMapParcels(true)}
            >
              <MapIcon sx={{ fontSize: 18 }} />
              {t('common.map')}
            </button>
          </div>
        )}

        {!viewMapParcels ? (
          <div className="parcelsList">
            {filteredParcels
              ?.filter((parcel) => !parcel.parent)
              .map((parcel) => (
                <ModernCheckbox
                  key={parcel.id}
                  label={getParcelLabel(parcel)}
                  checked={selectedParcels.includes(parcel.id)}
                  onChange={() => toggleSelection(parcel.id, selectedParcels, setSelectedParcels)}
                />
              ))}
          </div>
        ) : (
          <div className="parcelsMap">
            <MapParamsSimulation
              updateParcel={(e) => toggleSelection(e, selectedParcels, setSelectedParcels)}
              forecastCampaign={exploitationSelected?.forecastCampaign}
              name="map_parcels"
              fromProcedure={true}
              parcelListData={filteredParcels}
              selectedParcels={selectedParcels}
            />

            <div className="mapLegend">
              <div className="legendItem">
                <div className="legendColor legendColorSelected"></div>
                <span>{t('simulation.step.parcels.selected')}</span>
              </div>
              <div className="legendItem">
                <div className="legendColor legendColorUnselected"></div>
                <span>{t('simulation.step.parcels.unselected')}</span>
              </div>
            </div>
          </div>
        )}

        <div className="infoBadge">
          <p>
            <strong>{selectedParcels.length}</strong> {t('simulation.step.parcels.selectedCount')}
          </p>
        </div>
      </ModernCard>
    </div>
  );
};

export default StepParcels;
